import { Component } from '@theme/component';

/**
 * Milliseconds of quiet after a manual scroll or drag before the drift resumes.
 * @type {number}
 */
const RESUME_DELAY = 1500;

/**
 * Tolerance, in pixels, between the position we last wrote and the position the
 * scroller reports. Anything larger is treated as the visitor scrolling.
 * @type {number}
 */
const SCROLL_EPSILON = 2;

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A horizontal row of product media that drifts slowly from right to left and
 * can be taken over by the visitor at any time.
 *
 * The track is a native `overflow-x: auto` scroller, so wheel, trackpad, touch
 * and keyboard scrolling work on their own. This component adds:
 *   - the automatic drift, by advancing `scrollLeft` on every animation frame
 *   - a seamless wrap, by cloning the list once and subtracting the loop width
 *   - pointer drag, so a mouse without horizontal scroll can move the row too
 *
 * The drift is suspended while the visitor interacts, while the section is off
 * screen, and entirely when the visitor prefers reduced motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scroll container.
 * @property {HTMLElement} list - The list of media items.
 *
 * @extends Component<Refs>
 */
class CspMoodboardComponent extends Component {
  requiredRefs = ['track', 'list'];

  /** @type {HTMLElement | null} */
  #clone = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {number | null} */
  #resumeTimer = null;

  /** Distance between the start of the list and the start of its clone. */
  #loopWidth = 0;

  /** The scroll position we own, kept as a float so slow speeds don't stall. */
  #position = 0;

  /** Timestamp of the previous animation frame. */
  #lastFrameTime = 0;

  #interacting = false;
  #visible = true;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {{ pointerId: number, x: number } | null} */
  #drag = null;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;

    this.#measure();

    track.addEventListener('scroll', this.#handleScroll, { passive: true });
    track.addEventListener('pointerdown', this.#handlePointerDown);
    track.addEventListener('pointermove', this.#handlePointerMove);
    track.addEventListener('pointerup', this.#handlePointerUp);
    track.addEventListener('pointercancel', this.#handlePointerUp);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resumeNow);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resumeNow);

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(track);
    // Videos have no intrinsic size until their metadata loads, which widens the list.
    this.#resizeObserver.observe(this.refs.list);

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#visible = entry?.isIntersecting ?? true;
    });
    this.#intersectionObserver.observe(this);

    reducedMotion.addEventListener('change', this.#handleMotionPreferenceChange);

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { track } = this.refs;

    this.#stop();

    track.removeEventListener('scroll', this.#handleScroll);
    track.removeEventListener('pointerdown', this.#handlePointerDown);
    track.removeEventListener('pointermove', this.#handlePointerMove);
    track.removeEventListener('pointerup', this.#handlePointerUp);
    track.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resumeNow);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resumeNow);

    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    reducedMotion.removeEventListener('change', this.#handleMotionPreferenceChange);

    if (this.#resumeTimer != null) clearTimeout(this.#resumeTimer);
  }

  /** Pixels per second, from the section setting. 0 disables the drift. */
  get #speed() {
    return Number(this.dataset.speed ?? 0);
  }

  get #shouldDrift() {
    return this.#speed > 0 && this.#loopWidth > 0 && !reducedMotion.matches;
  }

  /**
   * Adds or removes the duplicate list depending on whether the content
   * overflows, then records the loop width. Without overflow there is nothing
   * to scroll, so the clone would only duplicate media for no reason.
   */
  #measure() {
    const { track, list } = this.refs;
    const overflows = list.offsetWidth > track.clientWidth;

    if (overflows && !this.#clone) {
      const clone = /** @type {HTMLElement} */ (list.cloneNode(true));
      clone.removeAttribute('ref');
      clone.setAttribute('aria-hidden', 'true');
      for (const focusable of clone.querySelectorAll('a, button, video')) {
        focusable.setAttribute('tabindex', '-1');
      }
      track.appendChild(clone);
      this.#clone = clone;
    } else if (!overflows && this.#clone) {
      this.#clone.remove();
      this.#clone = null;
    }

    // Distance from one copy to the next, which includes the flex gap.
    this.#loopWidth = this.#clone ? this.#clone.offsetLeft - list.offsetLeft : 0;
    this.#position = track.scrollLeft;
  }

  #start() {
    if (this.#frame != null) return;
    this.#lastFrameTime = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#frame == null) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /**
   * @param {number} now
   */
  #tick = (now) => {
    const elapsed = now - this.#lastFrameTime;
    this.#lastFrameTime = now;

    if (this.#shouldDrift && this.#visible && !this.#interacting) {
      const { track } = this.refs;

      this.#position += (this.#speed * elapsed) / 1000;
      this.#position = this.#wrap(this.#position);
      track.scrollLeft = this.#position;
    }

    this.#frame = requestAnimationFrame(this.#tick);
  };

  /**
   * Keeps a position inside a single copy of the list, so the clone makes the
   * jump invisible. Works in both directions, for dragging backwards.
   *
   * @param {number} position
   * @returns {number}
   */
  #wrap(position) {
    const loopWidth = this.#loopWidth;
    if (loopWidth <= 0) return position;

    if (position >= loopWidth) return position - loopWidth;
    if (position < 0) return position + loopWidth;
    return position;
  }

  #handleScroll = () => {
    const { track } = this.refs;

    // Our own writes land within a pixel of the position we asked for; anything
    // further apart came from the visitor.
    if (Math.abs(track.scrollLeft - this.#position) <= SCROLL_EPSILON) return;

    this.#position = track.scrollLeft;
    this.#pause();
    this.#scheduleResume();
  };

  #pause = () => {
    this.#interacting = true;
    if (this.#resumeTimer != null) {
      clearTimeout(this.#resumeTimer);
      this.#resumeTimer = null;
    }
  };

  #resumeNow = () => {
    if (this.#drag) return;
    this.#interacting = false;
    this.#position = this.refs.track.scrollLeft;
  };

  #scheduleResume() {
    if (this.#resumeTimer != null) clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => {
      this.#resumeTimer = null;
      this.#resumeNow();
    }, RESUME_DELAY);
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return; // Native touch scrolling is better.
    if (event.button !== 0) return;

    const { track } = this.refs;

    this.#drag = { pointerId: event.pointerId, x: event.clientX };
    track.setPointerCapture(event.pointerId);
    track.dataset.dragging = '';
    this.#pause();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const { track } = this.refs;
    const delta = event.clientX - drag.x;

    drag.x = event.clientX;
    this.#position = this.#wrap(this.#position - delta);
    track.scrollLeft = this.#position;
    // Re-read so a clamp at either end (no clone, nothing to wrap into) does
    // not leave our position out of sync with the scroller.
    this.#position = track.scrollLeft;
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const { track } = this.refs;

    track.releasePointerCapture(event.pointerId);
    delete track.dataset.dragging;
    this.#drag = null;
    this.#scheduleResume();
  };

  #handleMotionPreferenceChange = () => {
    if (reducedMotion.matches) return;
    this.#position = this.refs.track.scrollLeft;
  };
}

if (!customElements.get('csp-moodboard-component')) {
  customElements.define('csp-moodboard-component', CspMoodboardComponent);
}
